import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, Calendar, Download, RefreshCw, BarChart2, TrendingUp, Cpu, HelpCircle, Save, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import API from '../api';
import { useActiveOrg } from '../contexts/ActiveOrgContext';

const Reports = () => {
    const { t, i18n } = useTranslation();
    const { activeOrg } = useActiveOrg();
    const [month, setMonth] = useState(() => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        return `${yyyy}-${mm}`;
    });
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [manualEfforts, setManualEfforts] = useState({}); // { job_id: minutes }
    const [animateTotals, setAnimateTotals] = useState(false);

    // Fetch reports data when month or activeOrg changes
    useEffect(() => {
        if (activeOrg?.id) {
            fetchReportData();
        }
    }, [month, activeOrg]);

    const fetchReportData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get(`/reports/rpa-summary/?month=${month}&org_id=${activeOrg.id}&_t=${Date.now()}`);
            setReportData(res.data.data || []);
            
            // Set initial manual efforts to 0 or manual_time_minutes from jobs if we want default values
            // We'll initialize them with 0 or keep existing if user already started editing
            const initialEfforts = {};
            res.data.data.forEach(item => {
                initialEfforts[item.job_id] = manualEfforts[item.job_id] !== undefined ? manualEfforts[item.job_id] : 0;
            });
            setManualEfforts(initialEfforts);
        } catch (err) {
            console.error("Fetch report data error:", err);
            setError(err.response?.data?.error || t('reports.error_fetch', 'Rapor verileri alınırken bir hata oluştu.'));
        } finally {
            setLoading(false);
        }
    };

    // Handle manual effort input change
    const handleEffortChange = (jobId, val) => {
        // Parse value as integer or 0
        const minutes = val === '' ? '' : Math.max(0, parseInt(val, 10) || 0);
        setManualEfforts(prev => ({
            ...prev,
            [jobId]: minutes
        }));
        
        // Trigger temporary animation on totals
        setAnimateTotals(true);
        setTimeout(() => setAnimateTotals(false), 500);
    };

    // Excel Download Handler
    const handleDownloadExcel = async () => {
        if (!activeOrg?.id) return;
        setDownloading(true);
        setError(null);
        try {
            // Convert efforts to integer for safety
            const cleanedEfforts = {};
            Object.keys(manualEfforts).forEach(id => {
                cleanedEfforts[id] = parseInt(manualEfforts[id], 10) || 0;
            });

            const response = await API.post('/reports/rpa-summary/download/', {
                org_id: activeOrg.id,
                month: month,
                manual_efforts: cleanedEfforts
            }, { responseType: 'blob' });

            // Trigger file download
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `RPA_Genel_Raporu_${activeOrg.name}_${month}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Excel download error:", err);
            setError(t('reports.error_download', 'Excel dosyası indirilirken bir hata oluştu.'));
        } finally {
            setDownloading(false);
        }
    };

    // Calculations for the table rows
    const calculateRow = (item) => {
        const manualEffortVal = manualEfforts[item.job_id] === '' ? 0 : (manualEfforts[item.job_id] || 0);
        const processRealEffort = manualEffortVal * item.total_runs;
        const totalRobotTime = item.total_runs * item.avg_runtime_minutes;
        const savedTime = processRealEffort - totalRobotTime;
        const timeSavingsRatio = processRealEffort > 0 ? (savedTime / processRealEffort) * 100 : 0;
        const productivityIncrease = totalRobotTime > 0 ? (savedTime / totalRobotTime) * 100 : 0;

        return {
            processRealEffort,
            totalRobotTime,
            savedTime,
            timeSavingsRatio,
            productivityIncrease
        };
    };

    // Calculations for the totals row
    const getTotals = () => {
        let totalManualEffort = 0;
        let totalProcessRealEffort = 0;
        let totalRuns = 0;
        let totalRobotTime = 0;
        let totalSavedTime = 0;

        reportData.forEach(item => {
            const rowCalc = calculateRow(item);
            const manualEffortVal = manualEfforts[item.job_id] === '' ? 0 : (manualEfforts[item.job_id] || 0);
            totalManualEffort += manualEffortVal;
            totalProcessRealEffort += rowCalc.processRealEffort;
            totalRuns += item.total_runs;
            totalRobotTime += rowCalc.totalRobotTime;
            totalSavedTime += rowCalc.savedTime;
        });

        const overallTimeSavingsRatio = totalProcessRealEffort > 0 ? (totalSavedTime / totalProcessRealEffort) * 100 : 0;
        const overallProductivityIncrease = totalRobotTime > 0 ? (totalSavedTime / totalRobotTime) * 100 : 0;

        return {
            totalManualEffort,
            totalProcessRealEffort,
            totalRuns,
            totalRobotTime,
            totalSavedTime,
            overallTimeSavingsRatio,
            overallProductivityIncrease
        };
    };

    const totals = getTotals();

    // Check if organization is selected
    if (!activeOrg) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <FileSpreadsheet className="w-16 h-16 text-blue-500 mb-4 animate-pulse" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">{t('common.noOrgSelected', 'Organizasyon Seçilmedi')}</h2>
                <p className="text-gray-500 max-w-md">
                    {t('common.noOrgSelectedDesc', 'Raporları görüntülemek ve Excel dosyası oluşturabilmek için lütfen üst menüden aktif bir organizasyon seçin.')}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header section with month selection */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white p-6 rounded-2xl shadow-lg border border-blue-900/50">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-600/30 rounded-xl border border-blue-500/20 backdrop-blur-md">
                        <FileSpreadsheet className="w-8 h-8 text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">{t('reports.title', 'RPA Süreç Verimlilik Raporları')}</h1>
                        <p className="text-slate-400 text-sm mt-0.5">{t('reports.subtitle_with_org', '{{org}} organizasyonu için aylık performans analizi', { org: activeOrg.name })}</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    {/* Month Picker */}
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 pointer-events-none" />
                        <input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            className="bg-slate-800/80 border border-slate-700/80 text-white rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                        />
                    </div>

                    {/* Refresh Button */}
                    <button
                        onClick={fetchReportData}
                        disabled={loading}
                        className="p-2.5 bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl border border-slate-700/80 transition-colors disabled:opacity-50"
                        title={t('reports.refresh', 'Verileri Yenile')}
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-blue-400' : ''}`} />
                    </button>

                    {/* Excel Download Button */}
                    <button
                        onClick={handleDownloadExcel}
                        disabled={downloading || reportData.length === 0}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800/40 text-white rounded-xl font-semibold text-sm transition-all shadow-md hover:shadow-emerald-500/20 disabled:cursor-not-allowed border border-emerald-500/20"
                    >
                        {downloading ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                {t('reports.preparing', 'Hazırlanıyor...')}
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                {t('reports.download', 'Excel Olarak İndir')}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {error && (
                <div className="p-4 bg-red-950/40 border border-red-900/50 rounded-2xl text-red-300 text-sm flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200 font-semibold">{t('common.close', 'Kapat')}</button>
                </div>
            )}

            {/* KPI Dashboard Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('reports.total_runs_label', 'Toplam Başarılı Çalışma')}</span>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{totals.totalRuns.toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US')}</h3>
                        <p className="text-xs text-gray-500 mt-1">{t('reports.total_runs_desc', 'İlgili ay içindeki başarılı tetiklenmeler')}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                        <Cpu className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('reports.total_robot_time_label', 'Toplam Robot Zamanı')}</span>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">
                            {totals.totalRobotTime >= 60 
                                ? `${Math.floor(totals.totalRobotTime / 60)} ${t('reports.hours_short', 'sa')} ${Math.round(totals.totalRobotTime % 60)} ${t('reports.minutes_short', 'dk')}`
                                : `${Math.round(totals.totalRobotTime)} ${t('reports.minutes_short', 'dk')}`
                            }
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{t('reports.total_robot_time_desc', 'Robotun aktif çalıştığı toplam süre')}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-xl text-purple-600">
                        <Clock className="w-6 h-6" />
                    </div>
                </div>

                <div className={`bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between transition-all hover:shadow-md ${animateTotals ? 'ring-2 ring-emerald-100 bg-emerald-50/10' : ''}`}>
                    <div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('reports.total_saved_time_label', 'Kazanılan Toplam Zaman')}</span>
                        <h3 className={`text-2xl font-bold mt-1 transition-all ${totals.totalSavedTime >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {totals.totalSavedTime >= 0 
                                ? (totals.totalSavedTime >= 60 
                                    ? `+${Math.floor(totals.totalSavedTime / 60)} ${t('reports.hours_short', 'sa')} ${Math.round(totals.totalSavedTime % 60)} ${t('reports.minutes_short', 'dk')}`
                                    : `+${Math.round(totals.totalSavedTime)} ${t('reports.minutes_short', 'dk')}`)
                                : (Math.abs(totals.totalSavedTime) >= 60
                                    ? `-${Math.floor(Math.abs(totals.totalSavedTime) / 60)} ${t('reports.hours_short', 'sa')} ${Math.round(Math.abs(totals.totalSavedTime) % 60)} ${t('reports.minutes_short', 'dk')}`
                                    : `-${Math.round(Math.abs(totals.totalSavedTime))} ${t('reports.minutes_short', 'dk')}`)
                            }
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{t('reports.total_saved_time_desc', 'Efor tasarrufu sonucu kazanılan süre')}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${totals.totalSavedTime >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                        <TrendingUp className="w-6 h-6" />
                    </div>
                </div>

                <div className={`bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between transition-all hover:shadow-md ${animateTotals ? 'ring-2 ring-blue-100 bg-blue-50/10' : ''}`}>
                    <div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('reports.avg_productivity_gain_label', 'Ortalama Verimlilik Artışı')}</span>
                        <h3 className={`text-2xl font-bold mt-1 ${totals.overallProductivityIncrease >= 0 ? 'text-blue-600' : 'text-rose-600'}`}>
                            {totals.overallProductivityIncrease.toFixed(1)}%
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{t('reports.avg_productivity_gain_desc', 'Robot kullanımının getirdiği hız çarpanı')}</p>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
                        <BarChart2 className="w-6 h-6" />
                    </div>
                </div>
            </div>

            {/* Explanatory Info Card */}
            <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4 flex gap-3 text-sm text-blue-800">
                <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                    <span className="font-semibold">{t('reports.how_to_use', 'Nasıl Kullanılır?')}</span> {t('reports.how_to_use_desc', 'Aşağıdaki tabloda yer alan "Ortalama Süreç Gerçek Efor (DK)" kolonundaki değerleri manuel olarak düzenleyebilirsiniz. Bu değerler, robot olmasaydı bir insanın bu işi tek seferde yapmak için harcayacağı ortalama süreyi temsil eder. Girdiğiniz değerlere göre kazanılan zaman ve verimlilik oranları anlık olarak hesaplanır ve Excel çıktısına yansıtılır. Girdiğiniz bilgiler veritabanına kaydedilmez, sadece rapor oluşturma amaçlı anlık kullanılır.')}
                </div>
            </div>

            {/* Report Table Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-gray-500 flex flex-col items-center justify-center gap-3">
                        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                        <span>{t('common.loading', 'Veriler yükleniyor...')}</span>
                    </div>
                ) : reportData.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <span className="font-semibold text-gray-700 block">{t('common.no_data', 'Kayıt Bulunamadı')}</span>
                        <span className="text-sm mt-1 block">{t('reports.no_logs_found', 'Bu organizasyon için seçilen ayda çalışmış hiçbir süreç logu bulunamadı.')}</span>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-gray-100 text-gray-700 font-semibold">
                                    <th className="px-4 py-3.5">{t('reports.col_dept', 'Departman')}</th>
                                    <th className="px-4 py-3.5">{t('reports.col_name', 'Süreç Adı')}</th>
                                    <th className="px-4 py-3.5 text-center">{t('reports.col_start', 'Başlangıç')}</th>
                                    <th className="px-4 py-3.5 text-center">{t('reports.col_end', 'Bitiş')}</th>
                                    <th className="px-4 py-3.5 text-center w-36">{t('reports.col_manual', 'Ortalama Manuel Efor (DK)')}</th>
                                    <th className="px-4 py-3.5 text-right">{t('reports.col_real', 'Süreç Gerçek Efor (DK)')}</th>
                                    <th className="px-4 py-3.5 text-center">{t('reports.col_runs', 'Çalışma Sayısı')}</th>
                                    <th className="px-4 py-3.5 text-right">{t('reports.col_avg_robot', 'Ortalama Robot Süresi (DK)')}</th>
                                    <th className="px-4 py-3.5 text-right">{t('reports.col_total_robot', 'Toplam Robot Zamanı (DK)')}</th>
                                    <th className="px-4 py-3.5 text-right">{t('reports.col_saved', 'Kazanılan Zaman (DK)')}</th>
                                    <th className="px-4 py-3.5 text-right">{t('reports.col_saved_ratio', 'Zaman Kazancı Oranı')}</th>
                                    <th className="px-4 py-3.5 text-right">{t('reports.col_prod', 'Verimlilik Artışı')}</th>
                                    <th className="px-4 py-3.5 text-center">{t('reports.col_unit', 'Birim')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-gray-600">
                                {reportData.map((item) => {
                                    const calc = calculateRow(item);
                                    const effortVal = manualEfforts[item.job_id] !== undefined ? manualEfforts[item.job_id] : 0;

                                    return (
                                        <tr key={item.job_id} className="hover:bg-slate-50/50 transition-colors">
                                            {/* Department */}
                                            <td className="px-4 py-3 font-medium text-gray-800">
                                                {item.department || <span className="text-gray-400 italic">{t('jobs.not_specified', 'Belirtilmemiş')}</span>}
                                            </td>
                                            {/* Job Name */}
                                            <td className="px-4 py-3 font-medium text-slate-900">{item.job_name}</td>
                                            {/* Start Date */}
                                            <td className="px-4 py-3 text-center text-xs text-gray-400 whitespace-nowrap">{item.start_date}</td>
                                            {/* End Date */}
                                            <td className="px-4 py-3 text-center text-xs text-gray-400 whitespace-nowrap">{item.end_date}</td>
                                            {/* Input: Avg Manual Effort */}
                                            <td className="px-4 py-3 text-center">
                                                <div className="relative inline-block w-full">
                                                    <input
                                                        type="number"
                                                        value={effortVal}
                                                        min="0"
                                                        onChange={(e) => handleEffortChange(item.job_id, e.target.value)}
                                                        className="w-full text-center py-1.5 px-2 border border-gray-200 rounded-lg text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </td>
                                            {/* Process Real Effort */}
                                            <td className="px-4 py-3 text-right font-bold text-gray-800">
                                                {Math.round(calc.processRealEffort).toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US')}
                                            </td>
                                            {/* Total Runs */}
                                            <td className="px-4 py-3 text-center font-medium text-gray-800">{item.total_runs}</td>
                                            {/* Avg Robot Runtime */}
                                            <td className="px-4 py-3 text-right font-medium text-gray-800">
                                                {item.avg_runtime_minutes.toFixed(2)}
                                            </td>
                                            {/* Total Robot Time */}
                                            <td className="px-4 py-3 text-right font-bold text-gray-800">
                                                {Math.round(calc.totalRobotTime).toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US')}
                                            </td>
                                            {/* Saved Time */}
                                            <td className={`px-4 py-3 text-right font-bold ${calc.savedTime >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                {calc.savedTime >= 0 ? '+' : ''}
                                                {Math.round(calc.savedTime).toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US')}
                                            </td>
                                            {/* Time Savings Ratio */}
                                            <td className={`px-4 py-3 text-right font-bold ${calc.savedTime >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                {calc.timeSavingsRatio.toFixed(1)}%
                                            </td>
                                            {/* Productivity Increase */}
                                            <td className="px-4 py-3 text-right font-bold text-indigo-600">
                                                {calc.productivityIncrease.toFixed(1)}%
                                            </td>
                                            {/* Unit */}
                                            <td className="px-4 py-3 text-center text-xs text-gray-400">{t('reports.unit_minutes', 'Dakika')}</td>
                                        </tr>
                                    );
                                })}

                                {/* Table Totals Row */}
                                <tr className={`bg-slate-50 font-bold border-t-2 border-slate-200 text-slate-800 transition-all duration-300 ${animateTotals ? 'bg-blue-50/50' : ''}`}>
                                    <td colSpan="4" className="px-4 py-4 text-center tracking-wider text-sm font-extrabold uppercase text-slate-500">
                                        {t('reports.total', 'TOPLAM')}
                                    </td>
                                    {/* Total Manual Effort */}
                                    <td className="px-4 py-4 text-center text-slate-900 font-extrabold text-base">
                                        {totals.totalManualEffort}
                                    </td>
                                    {/* Total Process Real Effort */}
                                    <td className="px-4 py-4 text-right text-slate-900 font-extrabold text-base">
                                        {Math.round(totals.totalProcessRealEffort).toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US')}
                                    </td>
                                    {/* Total Runs */}
                                    <td className="px-4 py-4 text-center font-extrabold text-base">
                                        {totals.totalRuns.toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US')}
                                    </td>
                                    {/* Avg Robot Runtime (N/A for totals) */}
                                    <td className="px-4 py-4 text-right text-gray-400 font-normal italic">-</td>
                                    {/* Total Robot Time */}
                                    <td className="px-4 py-4 text-right text-slate-900 font-extrabold text-base">
                                        {Math.round(totals.totalRobotTime).toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US')}
                                    </td>
                                    {/* Total Saved Time */}
                                    <td className={`px-4 py-4 text-right font-extrabold text-base ${totals.totalSavedTime >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {totals.totalSavedTime >= 0 ? '+' : ''}
                                        {Math.round(totals.totalSavedTime).toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US')}
                                    </td>
                                    {/* Overall Time Savings Ratio */}
                                    <td className={`px-4 py-4 text-right font-extrabold text-base ${totals.totalSavedTime >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {totals.overallTimeSavingsRatio.toFixed(1)}%
                                    </td>
                                    {/* Overall Productivity Increase */}
                                    <td className="px-4 py-4 text-right font-extrabold text-indigo-600 text-base">
                                        {totals.overallProductivityIncrease.toFixed(1)}%
                                    </td>
                                    {/* Unit */}
                                    <td className="px-4 py-4 text-center text-xs text-slate-400 font-normal">{t('reports.unit_minutes', 'Dakika')}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Reports;
